import React, { useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ActionButton, AlertDialog, Checkbox, DialogTrigger, Flex } from '@adobe/react-spectrum';
import './ManageDecks.css';
import { Helmet } from 'react-helmet';
import { BreadCrumbs } from '../breadcrumbs/BreadCrumbs';
import { BreadCrumbProfileNavBar } from '../breadcrumbs/buttons/BreadCrumbProfileNavBar';
import { postManageDecksListOperation } from '../../data/redux/slices/profileSlice';
import { GenericLoadingDialog } from '../genericLoadingDialog/GenericLoadingDialog';
import HeroPanel, { HeroPanelChip, HeroPanelTotal } from '../common/heroPanel/HeroPanel';

export function ManageDecks() {
    const dispatch = useDispatch();

    const isFetching = useSelector((state) => state?.profile?.isFetching);
    const decks = useSelector((state) => state?.profile?.manageDecks?.decks || []);

    const helmetName = `Manage Decks`;
    const helmetCanonical = `https://www.commandersalt.com/managedecks`;
    const helmetDescription = `The saltiest MtG deck builders on the planet!`;
    const helmetTitle = `Commandersalt.com - The saltiest MtG deck builders on the planet!`;

    const [selectedIds, setSelectedIds] = useState(new Set());
    const [filter, setFilter] = useState('');

    const filteredDecks = useMemo(() => {
        const q = filter.trim().toLowerCase();
        if (!q) return decks;
        return decks.filter((d) => (d.title || '').toLowerCase().includes(q));
    }, [decks, filter]);

    const selectedDecks = useMemo(
        () => decks.filter((d) => selectedIds.has(d.id)),
        [decks, selectedIds]
    );

    const allFilteredSelected =
        filteredDecks.length > 0 && filteredDecks.every((d) => selectedIds.has(d.id));
    const someFilteredSelected =
        filteredDecks.length > 0 && filteredDecks.some((d) => selectedIds.has(d.id));

    const toggleAll = () => {
        const next = new Set(selectedIds);
        if (allFilteredSelected) {
            filteredDecks.forEach((d) => next.delete(d.id));
        } else {
            filteredDecks.forEach((d) => next.add(d.id));
        }
        setSelectedIds(next);
    };

    const toggleOne = (id) => {
        const next = new Set(selectedIds);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        setSelectedIds(next);
    };

    const performOp = (op) => {
        dispatch(
            postManageDecksListOperation(
                op,
                selectedDecks.map((d) => d.id)
            )
        );
        setSelectedIds(new Set());
    };

    // For bulk visibility / author toggles, decide which direction to apply
    // based on the *minority* state — i.e. if some are private and some are
    // public, the action makes them all private (same logic as before).
    const allPrivate = selectedDecks.length > 0 && selectedDecks.every((d) => d.isPrivate);
    const allHideAuthor = selectedDecks.length > 0 && selectedDecks.every((d) => d.hideAuthorInfo);
    const visibilityAction = allPrivate ? 'mark_public' : 'mark_private';
    const visibilityLabel = allPrivate ? 'Make public' : 'Make private';
    const authorAction = allHideAuthor ? 'show_author' : 'hide_author';
    const authorLabel = allHideAuthor ? 'Show author info' : 'Hide author info';

    const selectedCount = selectedIds.size;
    const total = decks.length;
    const publicCount = decks.filter((d) => !d.isPrivate).length;
    const privateCount = total - publicCount;

    const deleteWarningText = `Permanently remove ${
        selectedCount > 1 ? `${selectedCount} decks` : 'this deck'
    } from Commandersalt? Removed decks will no longer be tracked or analysed.`;

    return (
        <Flex direction="column">
            <Helmet>
                <title>{helmetName}</title>
                <meta name="creator" content="Ben Rimbey" />
                <link rel="canonical" href={helmetCanonical} />
                <meta name="description" content={helmetDescription} />
                <meta name="twitter:title" content={helmetTitle} />
                <meta property="og:site_name" content="Commandersalt.com" />
                <meta property="og:title" content={helmetTitle} />
                <meta property="og:image" content="https://www.commandersalt.com/chef-kiss-meta-image.png" />
                <meta property="og:description" content={helmetDescription} />
            </Helmet>
            <BreadCrumbs content={<BreadCrumbProfileNavBar />} />
            {isFetching && <GenericLoadingDialog title="Loading decks..." />}

            <div className="manageDecksPage">
                <HeroPanel
                    tone="red"
                    className="manageDecksHero"
                    eyebrow="Deck Management"
                    title="Your decks"
                    hint="Bulk-flip visibility, author attribution, or remove decks. Selection survives the filter, so you can stage edits across queries."
                    aside={
                        <>
                            <HeroPanelTotal label="Decks" value={total.toLocaleString()} />
                            <HeroPanelChip label="Public" value={publicCount.toLocaleString()} />
                            {privateCount > 0 && (
                                <HeroPanelChip
                                    accent="warm"
                                    label="Private"
                                    value={privateCount.toLocaleString()}
                                />
                            )}
                        </>
                    }
                />

                <div className="manageDecksControlBar">
                    <div className="manageDecksSearchWrap">
                        <span className="manageDecksSearchIcon" aria-hidden>
                            ⌕
                        </span>
                        <input
                            type="text"
                            className="manageDecksSearch"
                            placeholder="Filter decks by name…"
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            aria-label="Filter decks by name"
                        />
                        {filter && (
                            <button
                                type="button"
                                className="manageDecksSearchClear"
                                onClick={() => setFilter('')}
                                aria-label="Clear filter"
                            >
                                ×
                            </button>
                        )}
                    </div>
                    <div className="manageDecksSelectAllWrap">
                        <Checkbox
                            isSelected={allFilteredSelected}
                            isIndeterminate={someFilteredSelected && !allFilteredSelected}
                            onChange={toggleAll}
                            isDisabled={filteredDecks.length === 0}
                        >
                            <span className="manageDecksSelectAllLabel">
                                {allFilteredSelected ? 'Deselect all' : 'Select all'}
                                {filter ? ` (${filteredDecks.length} matching)` : ''}
                            </span>
                        </Checkbox>
                    </div>
                </div>

                <div
                    className={`manageDecksToolbar${selectedCount > 0 ? ' is-active' : ''}`}
                    aria-live="polite"
                >
                    <div className="manageDecksToolbarLeft">
                        <span className="manageDecksToolbarCount">
                            <span className="manageDecksToolbarCountValue">{selectedCount}</span>
                            <span className="manageDecksToolbarCountLabel">selected</span>
                        </span>
                        {selectedCount > 0 && (
                            <button
                                type="button"
                                className="manageDecksLinkBtn"
                                onClick={() => setSelectedIds(new Set())}
                            >
                                Clear
                            </button>
                        )}
                    </div>
                    <div className="manageDecksToolbarActions">
                        <button
                            type="button"
                            className="manageDecksBtn"
                            disabled={selectedCount < 1}
                            onClick={() => performOp(visibilityAction)}
                        >
                            {visibilityLabel}
                        </button>
                        <button
                            type="button"
                            className="manageDecksBtn"
                            disabled={selectedCount < 1}
                            onClick={() => performOp(authorAction)}
                        >
                            {authorLabel}
                        </button>
                        <DialogTrigger>
                            <ActionButton
                                aria-label="Delete"
                                isDisabled={selectedCount < 1}
                                UNSAFE_className="manageDecksBtn manageDecksBtn--danger"
                            >
                                Delete
                            </ActionButton>
                            <AlertDialog
                                variant="destructive"
                                title="Confirm Delete"
                                primaryActionLabel="Continue"
                                cancelLabel="Cancel"
                                onPrimaryAction={() => performOp('delete')}
                            >
                                {deleteWarningText}
                            </AlertDialog>
                        </DialogTrigger>
                    </div>
                </div>

                <div className="manageDecksList" role="list">
                    {filteredDecks.length === 0 ? (
                        <div className="manageDecksEmpty">
                            {decks.length === 0
                                ? "You haven't imported any decks yet."
                                : `No decks match "${filter}".`}
                        </div>
                    ) : (
                        filteredDecks.map((deck) => {
                            const checked = selectedIds.has(deck.id);
                            return (
                                <label
                                    key={deck.id}
                                    className={`manageDecksRow${checked ? ' is-selected' : ''}`}
                                    role="listitem"
                                >
                                    <Checkbox
                                        isSelected={checked}
                                        onChange={() => toggleOne(deck.id)}
                                        aria-label={`Select ${deck.title}`}
                                    />
                                    <span className="manageDecksRowName">{deck.title || 'Untitled deck'}</span>
                                    <div className="manageDecksRowChips">
                                        <span
                                            className={`manageDecksChip manageDecksChip--${
                                                deck.isPrivate ? 'private' : 'public'
                                            }`}
                                        >
                                            <span className="manageDecksChipLabel">Visibility</span>
                                            <span className="manageDecksChipValue">
                                                {deck.isPrivate ? 'Private' : 'Public'}
                                            </span>
                                        </span>
                                        <span
                                            className={`manageDecksChip manageDecksChip--${
                                                deck.hideAuthorInfo ? 'hidden' : 'shown'
                                            }`}
                                        >
                                            <span className="manageDecksChipLabel">Author</span>
                                            <span className="manageDecksChipValue">
                                                {deck.hideAuthorInfo ? 'Hidden' : 'Shown'}
                                            </span>
                                        </span>
                                    </div>
                                </label>
                            );
                        })
                    )}
                </div>
            </div>
        </Flex>
    );
}
